import { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, IconButton, useMediaQuery, useTheme, Grid, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Avatar, Stack, Chip, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DropdownProductCode from './DropdownProductCode';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';

const SIZE_PRESETS = {
    male: {
        label: 'Option 1 - Male',
        sizes: ['M', 'L', 'XL', 'XXL', 'XXXL']
    },
    kids: {
        label: 'Option 2 - Kids',
        sizes: ['20', '21', '22', '23', '24', '25', '26', '27', '28']
    },
    female: {
        label: 'Option 3 - Female',
        sizes: ['XS', 'S', 'M', 'L']
    }
};

// Example color list; replace/fetch as required
const DEFAULT_COLORS = [
    { id: '1', name: 'Red', hex: '#e53935' },
    { id: '2', name: 'Blue', hex: '#1e88e5' },
    { id: '3', name: 'Black', hex: '#000000' },
    { id: '4', name: 'White', hex: '#ffffff', border: '#cfcfcf' },
    { id: '5', name: 'Green', hex: '#43a047' },
    { id: '6', name: 'Yellow', hex: '#fdd835' },
];

const ProductDetails = ({ initialData, onNext, clientData, companyData, loggedInUser, connectionString }) => {
    const state = clientData?.details?.['State'] || '';
    const compState = companyData?.[0]?.State || '';
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));

    const [rows, setRows] = useState([]);
    const [nextsr, setNextsr] = useState(1);
    const [current, setCurrent] = useState({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, batchNo: '', expDate: '', serialNo: '', amount: '', taxAmount: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', TaxId: null });
    const [totals, setTotals] = useState({ basic: 0, discountAmount: 0, discountedAmount: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, total: 0 });
    const [errors, setErrors] = useState({});
    const [editIndex, setEditIndex] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [colorMap, setColorMap] = useState({});
    const [sizeMap, setSizeMap] = useState({});

    // new local UI states for size & color
    const [selectedSizePreset, setSelectedSizePreset] = useState('male');
    const [colorDialogOpen, setColorDialogOpen] = useState(false);
    const [availableColors, setAvailableColors] = useState(DEFAULT_COLORS);
    const [selectedColors, setSelectedColors] = useState([]);
    const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);
    const [tempMatrix, setTempMatrix] = useState({});
    const [matrixError, setMatrixError] = useState('');

    // states for size dialog
    const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        // fetch units/colors/size masters & products
        const fetchData = async () => {
            try {
                const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`);
                setUnitOptions(unitsRes.data || []);
                // color & size fetch
                const [cRes, sRes] = await Promise.allSettled([
                    axios.get(`${connectionString}/api/colormaster`),
                    axios.get(`${connectionString}/api/sizemaster`)
                ]);
                if (cRes.status === 'fulfilled' && Array.isArray(cRes.value.data)) {
                    const cm = {};
                    cRes.value.data.forEach(c => (cm[c.colorId] = { name: c.itemColor, code: c.colorCode }));
                    setColorMap(cm);
                    // Map to availableColors using numeric colorId
                    const fetchedColors = cRes.value.data.map(c => ({
                        id: String(c.colorId), // Use numeric colorId directly
                        name: c.itemColor || c.color || 'Unknown',
                        hex: c.colorCode || '#ccc'
                    }));
                    setAvailableColors(fetchedColors.length ? fetchedColors : DEFAULT_COLORS);
                }
                if (sRes.status === 'fulfilled' && Array.isArray(sRes.value.data)) {
                    const sm = {};
                    sRes.value.data.forEach(s => (sm[s.sizeId] = { name: s.itemSize, code: s.sizeCode }));
                    setSizeMap(sm);
                }
            } catch (error) {
                console.error('Error fetching product or unit data:', error);
            }
        };

        fetchData();
        loadAllProducts();
    }, []);

    const loadAllProducts = async () => {
        try {
            const res = await axios.get(`${connectionString}/api/productdata/retrive`);
            setAllProducts(res.data || []);
        } catch (e) {
            console.error('Error fetching products:', e);
        }
    };

    // keep your existing initialData -> rows mapping
    useEffect(() => {
        if (!initialData?.tableData || !Array.isArray(initialData.tableData) || !unitOptions.length || !allProducts.length) {
            return;
        }
        const initialRows = initialData.tableData.map((r, index) => {
            const qty = Number(r.qty) || 0;
            const rate = Number(r.rate) || 0;
            const disc = Number(r.disc) || 0;
            const taxableAmt = qty * rate * (1 - disc / 100);
            const product = allProducts.find(p => p.Id === r.productId || p.itemCode === r.productCode);
            let tax = Number(r.tax) || 0;
            let taxAmount = taxableAmt * (tax / 100);
            let cgst = '0.00', sgst = '0.00', igst = '0.00';
            let cgstAmt = '0.00', sgstAmt = '0.00', igstAmt = '0.00';
            let taxId = r.TaxId || null;
            const matchedUnit =
                unitOptions.find(u => String(u.UnitId) === String(r.UnitId)) ||
                unitOptions.find(u => u.unit?.toLowerCase() === String(r.unit).toLowerCase()) ||
                (product ? unitOptions.find(u => String(u.UnitId) === String(product.UnitId) || u.unit?.toLowerCase() === String(product.Unit?.toLowerCase())) : null);

            if (product) {
                tax = parseFloat(product.GST) || tax;
                taxId = product.TaxId || taxId;
                taxAmount = taxableAmt * (tax / 100);
                cgst = state === compState ? (tax / 2).toFixed(2) : '0.00';
                sgst = state === compState ? (tax / 2).toFixed(2) : '0.00';
                igst = state !== compState ? tax.toFixed(2) : '0.00';
                cgstAmt = state === compState ? (taxAmount / 2).toFixed(2) : '0.00';
                sgstAmt = state === compState ? (taxAmount / 2).toFixed(2) : '0.00';
                igstAmt = state !== compState ? taxAmount.toFixed(2) : '0.00';
            } else {
                taxAmount = taxableAmt * (tax / 100);
                cgst = String(r.CGST || (state === compState ? (tax / 2) : 0)).toFixed(2);
                sgst = String(r.SGST || (state === compState ? (tax / 2) : 0)).toFixed(2);
                igst = String(r.IGST || (state !== compState ? tax : 0)).toFixed(2);
                cgstAmt = Number(r.CGSTAmt || (state === compState ? (taxAmount / 2) : 0)).toFixed(2);
                sgstAmt = Number(r.SGSTAmt || (state === compState ? (taxAmount / 2) : 0)).toFixed(2);
                igstAmt = Number(r.IGSTAmt || (state !== compState ? taxAmount : 0)).toFixed(2);
            }

            // Map colors and sizeBreakdown with numeric colorId
            let colors = [];
            let sizeBreakdown = {};
            if (r.colors && Array.isArray(r.colors)) {
                colors = r.colors.map(c => ({
                    id: String(c.id),
                    name: c.name || 'Unknown',
                    hex: c.hex || '#ccc'
                }));
                sizeBreakdown = r.sizeBreakdown || r.sizeQtys || {};
            } else if (r.color && typeof r.color === 'object' && r.color.id !== undefined) {
                const colorObj = {
                    id: String(r.color.id),
                    name: r.color.name || 'Unknown',
                    hex: r.color.hex || '#ccc'
                };
                colors = [colorObj];
                sizeBreakdown = r.sizeQtys ? { [r.color.id]: r.sizeQtys } : {};
            } else if (r.sizeQtys) {
                if (Object.keys(r.sizeQtys).some(k => k !== 'default')) {
                    colors = Object.keys(r.sizeQtys).map(cId =>
                        availableColors.find(c => c.id === cId) || { id: cId, name: cId, hex: '#ccc' }
                    );
                    sizeBreakdown = r.sizeQtys;
                } else {
                    colors = [];
                    sizeBreakdown = { default: r.sizeQtys };
                }
            } else {
                colors = [];
                sizeBreakdown = {};
            }

            return {
                ...r,
                productId: r.productId || r.Id || r.id || '',
                UnitId: matchedUnit ? String(matchedUnit.UnitId) : (product?.UnitId || ''),
                unit: matchedUnit ? matchedUnit.unit : (product?.Unit || ''),
                qty,
                rate,
                disc,
                tax,
                TaxableAmt: Number(r.TaxableAmt) || taxableAmt.toFixed(2),
                taxAmount: Number(r.taxAmount) || taxAmount.toFixed(2),
                amount: Number(r.TaxableAmt) || taxableAmt.toFixed(2),
                CGST: cgst,
                SGST: sgst,
                IGST: igst,
                CGSTAmt: cgstAmt,
                SGSTAmt: sgstAmt,
                IGSTAmt: igstAmt,
                UTGST: String(r.UTGST || '0.00'),
                GSTCess: String(r.GSTCess || '0.00'),
                TaxId: taxId,
                sr: initialData.tableData.length - index,
                colors,
                sizeBreakdown
            };
        });

        setRows(initialRows);
        setNextsr(initialRows.length + 1);
    }, [initialData?.tableData, unitOptions, allProducts, state, compState]);

    // Recalculate current computed fields
    useEffect(() => {
        const qty = Number(current.qty) || 0;
        const rate = Number(current.rate) || 0;
        const disc = Number(current.disc) || 0;
        const tax = Number(current.tax) || 0;
        const taxableAmt = qty * rate * (1 - disc / 100);
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent(prev => ({
            ...prev,
            TaxableAmt: taxableAmt.toFixed(2),
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            CGST: state === compState ? (tax / 2).toFixed(2) : '0.00',
            SGST: state === compState ? (tax / 2).toFixed(2) : '0.00',
            IGST: state !== compState ? tax.toFixed(2) : '0.00',
            CGSTAmt: state === compState ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state === compState ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state !== compState ? taxAmount.toFixed(2) : '0.00'
        }));
    }, [current.qty, current.rate, current.disc, current.tax, state, compState]);

    // totals calculation (works on rows)
    useEffect(() => {
        const basic = rows.reduce((sum, r) => sum + Number(r.amount || r.TaxableAmt || 0), 0);
        const discountAmount = rows.reduce((sum, r) => sum + (Number(r.qty || 0) * Number(r.rate || 0) * Number(r.disc || 0) / 100), 0);
        const discountedAmount = basic - discountAmount;
        const cgst = rows.reduce((sum, r) => sum + Number(r.CGSTAmt || 0), 0);
        const sgst = rows.reduce((sum, r) => sum + Number(r.SGSTAmt || 0), 0);
        const igst = rows.reduce((sum, r) => sum + Number(r.IGSTAmt || 0), 0);
        const totalTax = cgst + sgst + igst;
        const finalAmount = basic + totalTax;
        const roundedFinal = Math.ceil(finalAmount);
        const roundOff = Number((roundedFinal - finalAmount).toFixed(2));

        setTotals({
            basic: basic.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            discountedAmount: discountedAmount.toFixed(2),
            cgst: cgst.toFixed(2),
            sgst: sgst.toFixed(2),
            igst: igst.toFixed(2),
            roundOff: roundOff.toFixed(2),
            total: roundedFinal.toFixed(2)
        });
    }, [rows]);

    useEffect(() => {
        if (matrixDialogOpen) {
            const preset = SIZE_PRESETS[selectedSizePreset];
            const initMatrix = {};
            if (editIndex !== null) {
                const row = rows[editIndex];
                let rowBreakdown = row.sizeBreakdown || {};
                if (!rowBreakdown && row.sizeQtys) {
                    rowBreakdown = { default: row.sizeQtys };
                }
                if (selectedColors.length > 0) {
                    selectedColors.forEach(cId => {
                        initMatrix[cId] = {};
                        preset.sizes.forEach(s => {
                            initMatrix[cId][s] = rowBreakdown[cId]?.[s] || 0;
                        });
                    });
                } else if (rowBreakdown.default) {
                    initMatrix.default = {};
                    preset.sizes.forEach(s => {
                        initMatrix.default[s] = rowBreakdown.default[s] || 0;
                    });
                }
            } else {
                selectedColors.forEach(cId => {
                    initMatrix[cId] = {};
                    preset.sizes.forEach(s => {
                        initMatrix[cId][s] = 0;
                    });
                });
            }
            setTempMatrix(initMatrix);
            setMatrixError('');
        }
    }, [matrixDialogOpen, selectedColors, selectedSizePreset, editIndex, rows]);

    const handleChange = field => e => {
        if (field === 'UnitId') {
            const selectedUnit = unitOptions.find(u => u.UnitId === Number(e.target.value));
            setCurrent(prev => ({ ...prev, UnitId: e.target.value, unit: selectedUnit ? selectedUnit.unit : '' }));
        } else {
            setCurrent(prev => ({ ...prev, [field]: e.target.value }));
        }
    };

    const handleSelect = product => {
        const matchedUnit = unitOptions.find(opt => {
            if (product.UnitId && opt.UnitId === product.UnitId) return true;
            if (product.Unit && opt.unit?.toLowerCase() === product.Unit.toLowerCase()) return true;
            if (product.unit && opt.unit?.toLowerCase() === product.unit.toLowerCase()) return true;
            return false;
        });
        const qty = 1;
        const rate = Number(product.saleRate) || 0;
        const disc = Number(product.saleDiscount) || 0;
        const tax = parseFloat(product.GST) || 0;
        const taxId = product.TaxId || null;
        const taxableAmt = qty * rate * (1 - disc / 100);
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent({
            Id: product.Id?.toString() || '',
            productCode: product.itemCode || '',
            description: product.description || '',
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : '',
            unit: matchedUnit ? matchedUnit.unit : '',
            qty,
            rate,
            disc,
            tax,
            batchNo: '',
            expDate: '',
            serialNo: '',
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            remark: '',
            TaxId: taxId,
            CGST: state === compState ? (tax / 2).toFixed(2) : '0.00',
            SGST: state === compState ? (tax / 2).toFixed(2) : '0.00',
            IGST: state !== compState ? tax.toFixed(2) : '0.00',
            UTGST: '0.00',
            GSTCess: '0.00',
            CGSTAmt: state === compState ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state === compState ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state !== compState ? taxAmount.toFixed(2) : '0.00',
            TaxableAmt: taxableAmt.toFixed(2)
        });
        setEditIndex(null);
        setErrors({});
    };

    const toggleColorSelection = colorId => {
        setSelectedColors(prev => {
            if (prev.includes(colorId)) return prev.filter(c => c !== colorId);
            return [...prev, colorId];
        });
    };

    const openColorDialog = () => setColorDialogOpen(true);
    const closeColorDialog = () => setColorDialogOpen(false);

    const handleOpenMatrix = () => {
        const errs = {};
        const messages = new Set();
        const requiredFields = ['productCode', 'description', 'UnitId', 'rate'];
        requiredFields.forEach(field => {
            if (!current[field] && current[field] !== 0) {
                errs[field] = 'Required';
                messages.add('All required fields must be filled.');
            }
        });
        const editRowData = editIndex !== null ? rows[editIndex] : null;
        if (editIndex !== null && (editRowData?.sizeBreakdown?.default || editRowData?.sizeQtys) && selectedColors.length === 0) {
            // Allow for legacy single color edit
        } else if (!selectedColors.length) {
            messages.add('Select at least one color.');
            errs.colors = 'Select colors';
        }
        if (messages.size > 0) {
            errs.global = Array.from(messages);
            setErrors(errs);
            return;
        }
        setErrors({});
        setMatrixDialogOpen(true);
    };

    const handleConfirmMatrix = () => {
        let hasQty = false;
        Object.values(tempMatrix).forEach(qtys => {
            const sum = Object.values(qtys).reduce((s, v) => s + Number(v || 0), 0);
            if (sum > 0) hasQty = true;
        });
        if (!hasQty) {
            setMatrixError('Enter quantities for at least one color.');
            return;
        }
        setMatrixError('');

        const rate = Number(current.rate) || 0;
        const disc = Number(current.disc) || 0;
        const tax = Number(current.tax) || 0;

        const totalQty = Object.values(tempMatrix).reduce((sum, colorQtys) => sum + Object.values(colorQtys).reduce((s, v) => s + Number(v || 0), 0), 0);
        const taxableAmt = totalQty * rate * (1 - disc / 100);
        const taxAmount = taxableAmt * (tax / 100);

        let colors = [];
        let sizeBreakdown = {};
        if (selectedColors.length > 0) {
            colors = selectedColors.map(colorId => {
                const colorObj = availableColors.find(c => c.id === colorId) || { id: colorId, name: colorId, hex: '#ccc' };
                return colorObj;
            });
            sizeBreakdown = { ...tempMatrix };
        } else if (tempMatrix.default) {
            colors = [];
            sizeBreakdown = { default: tempMatrix.default };
        }

        const row = {
            Id: current.Id || '',
            productId: current.Id || '',
            productCode: current.productCode,
            description: current.description,
            UnitId: current.UnitId || '',
            unit: current.unit || '',
            qty: totalQty,
            rate,
            disc,
            tax,
            remark: current.remark || '',
            TaxableAmt: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            amount: taxableAmt.toFixed(2),
            CGST: state === compState ? (tax / 2).toFixed(2) : '0.00',
            SGST: state === compState ? (tax / 2).toFixed(2) : '0.00',
            IGST: state !== compState ? tax.toFixed(2) : '0.00',
            CGSTAmt: state === compState ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state === compState ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state !== compState ? taxAmount.toFixed(2) : '0.00',
            UTGST: current.UTGST,
            GSTCess: current.GSTCess,
            TaxId: current.TaxId,
            sr: editIndex !== null ? rows[editIndex].sr : nextsr,
            colors,
            sizePresetKey: selectedSizePreset,
            sizePresetLabel: SIZE_PRESETS[selectedSizePreset].label,
            sizeBreakdown
        };

        if (editIndex != null) {
            setRows(prev => {
                const newRows = [...prev];
                newRows[editIndex] = row;
                return newRows;
            });
        } else {
            setRows(prev => [row, ...prev]);
            setNextsr(prev => prev + 1);
        }

        setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null });
        setSelectedColors([]);
        setErrors({});
        setEditIndex(null);
        setMatrixDialogOpen(false);
    };

    const editRow = idx => {
        const row = rows[idx];
        const matchedUnit = unitOptions.find(u => String(u.UnitId) === String(row.UnitId)) ||
            unitOptions.find(u => String(u.UnitId) === String(row.unit)) ||
            unitOptions.find(u => u.unit?.toLowerCase() === String(row.unit).toLowerCase());

        setCurrent({
            ...row,
            Id: row.productId,
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : row.UnitId || '',
            unit: matchedUnit ? matchedUnit.unit : row.unit || '',
            CGST: String(row.CGST || '0.00'),
            SGST: String(row.SGST || '0.00'),
            IGST: String(row.IGST || '0.00'),
            CGSTAmt: String(row.CGSTAmt || '0.00'),
            SGSTAmt: String(row.SGSTAmt || '0.00'),
            IGSTAmt: String(row.IGSTAmt || '0.00'),
            UTGST: String(row.UTGST || '0.00'),
            GSTCess: String(row.GSTCess || '0.00'),
            TaxableAmt: String(row.TaxableAmt || '0.00'),
            taxAmount: String(row.taxAmount || '0.00'),
            amount: String(row.amount || '0.00'),
            TaxId: row.TaxId || null
        });

        // restore sizes & color selection
        setSelectedSizePreset(row.sizePresetKey || 'male');
        const rowColors = row.colors || (row.color ? [row.color] : []);
        let editColors = rowColors.map(c => c.id);
        const rowBreakdown = row.sizeBreakdown || {};
        const breakdownKeys = Object.keys(rowBreakdown);
        if (editColors.length === 0 && breakdownKeys.length > 0) {
            editColors = breakdownKeys.filter(k => k !== 'default');
        } else if (editColors.length === 0 && row.sizeQtys) {
            // legacy default, do not set 'default'
        }
        setSelectedColors(editColors);
        setEditIndex(idx);
    };

    const deleteRow = idx => {
        setRows(prev => prev.filter((_, i) => i !== idx));
        if (idx === editIndex) {
            setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null });
            setEditIndex(null);
            setErrors({});
        }
    };

    const handleNext = () => {
        if (rows.length === 0) {
            setErrors({ global: 'Add at least one product' });
            return;
        }

        const getPresetLabel = (key) => {
            return SIZE_PRESETS[key || 'male']?.label || '';
        };

        const payloadRows = rows.map(r => {
            // Construct colorSizes array
            const colorSizes = [];
            const rowBreakdown = r.sizeBreakdown || (r.sizeQtys ? { default: r.sizeQtys } : {});
            const breakdownKeys = Object.keys(rowBreakdown);
            const explicitColors = r.colors || (r.color ? [r.color] : []);
            const allColorIds = explicitColors.length > 0 ? explicitColors.map(c => c.id) : breakdownKeys;

            allColorIds.forEach(cId => {
                const colorObj = cId === 'default' ? { id: cId, name: 'Default', hex: '#ccc' } : (availableColors.find(c => c.id === cId) || { id: cId, name: cId, hex: '#ccc' });
                const sizes = rowBreakdown[cId] || {};
                const total = Object.values(sizes).reduce((sum, qty) => sum + Number(qty || 0), 0);
                colorSizes.push({
                    color: colorObj.name,
                    colorId: Number(cId) || cId, // Ensure colorId is a number if possible
                    sizes: { ...sizes },
                    total
                });
            });

            return {
                productId: r.productId,
                productCode: r.productCode,
                description: r.description,
                unit: r.unit,
                UnitId: Number(r.UnitId),
                qty: r.qty,
                rate: r.rate,
                disc: r.disc,
                tax: r.tax,
                batchNo: r.batchNo,
                expDate: r.expDate,
                serialNo: r.serialNo,
                remark: r.remark,
                amount: Number(r.TaxableAmt),
                taxAmount: Number(r.taxAmount),
                sr: r.sr,
                TaxId: r.TaxId,
                CGST: r.CGST,
                SGST: r.SGST,
                IGST: r.IGST,
                CGSTAmt: Number(r.CGSTAmt),
                SGSTAmt: Number(r.SGSTAmt),
                IGSTAmt: Number(r.IGSTAmt),
                UTGST: r.UTGST,
                GSTCess: r.GSTCess,
                color: r.colors?.map(c => c.name).join(', ') || '',
                size: getPresetLabel(r.sizePresetKey),
                sizePresetKey: r.sizePresetKey || null,
                sizeQtys: r.sizeBreakdown || {},
                colorSizes, // Add colorSizes to the row
                colors: r.colors,
                sizeBreakdown: r.sizeBreakdown || {}
            };
        });

        const totalBasic = payloadRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
        const discountAmount = payloadRows.reduce((sum, r) => sum + (Number(r.qty || 0) * Number(r.rate || 0) * Number(r.disc || 0) / 100), 0);
        const discountedAmount = totalBasic - discountAmount;
        const cgst = payloadRows.reduce((sum, r) => sum + Number(r.CGSTAmt || 0), 0);
        const sgst = payloadRows.reduce((sum, r) => sum + Number(r.SGSTAmt || 0), 0);
        const igst = payloadRows.reduce((sum, r) => sum + Number(r.IGSTAmt || 0), 0);
        const totalTax = cgst + sgst + igst;
        const finalAmount = totalBasic + totalTax;
        const roundedFinal = Math.ceil(finalAmount);
        const roundOff = Number((roundedFinal - finalAmount).toFixed(2));

        const payload = {
            tableData: payloadRows,
            summary: {
                basic: Number(totalBasic.toFixed(2)),
                discount: Number(discountAmount.toFixed(2)),
                discountedAmount: Number(discountedAmount.toFixed(2)),
                cgst: Number(cgst.toFixed(2)),
                sgst: Number(sgst.toFixed(2)),
                igst: Number(igst.toFixed(2)),
                roundOff,
                total: roundedFinal
            }
        };
        onNext(payload);
    };

    const formatINR = val => {
        const num = Number(val) || 0;
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const openSizeDialog = (row) => {
        setSelectedRow(row);
        setSizeDialogOpen(true);
    };

    const closeSizeDialog = () => {
        setSizeDialogOpen(false);
        setSelectedRow(null);
    };

    // DataGrid columns: we render most product info inside a single 'product' column
    const columns = [
        { field: 'sr', headerName: '#', width: 20, align: 'center', headerAlign: 'center' },
        {
            field: 'product', headerName: 'Product', flex: 1, sortable: false, filterable: false,
            renderCell: params => {
                const r = params.row;
                const presetKey = r.sizePresetKey || 'male';
                const preset = SIZE_PRESETS[presetKey];
                const isSameState = state === compState;
                const rowBreakdown = r.sizeBreakdown || (r.sizeQtys ? { default: r.sizeQtys } : {});
                const explicitColors = r.colors || (r.color ? [r.color] : []);
                const displayColors = explicitColors.filter(c => c && c.id !== 'default');
                const allColorIds = explicitColors.length > 0 ? explicitColors.map(c => c.id) : Object.keys(rowBreakdown);
                const sizeTotals = {};
                preset.sizes.forEach(sz => { sizeTotals[sz] = allColorIds.reduce((sum, cId) => sum + Number(rowBreakdown[cId]?.[sz] || 0), 0); });
                const totalQty = Object.values(sizeTotals).reduce((s, v) => s + Number(v), 0);
                const hasSizes = totalQty > 0;

                // Compact tax tooltip text
                const taxTooltip = isSameState ? `SGST: ₹${formatINR(r.SGSTAmt)} + CGST: ₹${formatINR(r.CGSTAmt)}` : `IGST: ₹${formatINR(r.IGSTAmt)}`;

                return (
                    <Box sx={{ width: '100%', p: 1, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'background.default', borderRadius: 1, transition: 'box-shadow 0.2s' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                            <Stack direction="column" spacing={0.5} flexGrow={1}>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{
                                        cursor: 'pointer',
                                        color: '#1877F2',
                                        textDecoration: 'underline',
                                        fontSize: '0.9rem',
                                        '&:hover': { color: '#1565c0' }
                                    }}
                                    onClick={e => { e.stopPropagation(); editRow(params.row.index); }}
                                >
                                    {r.productCode}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{r.description}</Typography>
                                {/* Unit and GST chips */}
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {isSameState ? `SGST: ₹${formatINR(r.SGSTAmt)} + CGST: ₹${formatINR(r.CGSTAmt)}` : `IGST: ₹${formatINR(r.IGSTAmt)}`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Chip label={r.unit || 'N/A'} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 20 }} />
                                    <Chip label={`${r.tax}% GST`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.75rem', height: 20 }} />
                                    {hasSizes && (
                                        <IconButton
                                            size="small"
                                            onClick={e => { e.stopPropagation(); openSizeDialog(r); }}
                                            sx={{ p: 0.25, minWidth: 'auto' }}
                                        >
                                            <VisibilityIcon fontSize="small" sx={{ color: 'action.active' }} />
                                        </IconButton>
                                    )}
                                </Box>
                                {/* Colors below Unit and GST */}
                                {displayColors.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        {displayColors.map(c => (
                                            <Avatar
                                                key={c.id}
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    bgcolor: c.hex,
                                                    border: (c.hex === '#ffffff' || c.hex === '#ccc') ? '1px solid #cfcfcf' : undefined
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Stack>

                        </Box>
                    </Box>
                );
            }
        },
        { field: 'rate', headerName: 'Rate', flex: 0.5, align: 'right', headerAlign: 'center', renderCell: params => `₹${formatINR(params.row.rate)}` },
        {
            field: 'disc', width: 60, align: 'center', headerAlign: 'center', renderHeader: () => (
                <>
                    Disc <br /> (%)
                </>
            ),
        },
        { field: 'qty', headerName: 'Total Qty', width: 80, align: 'center', headerAlign: 'center' },
        { field: 'amount', headerName: 'Amount ₹', width: 110, align: 'right', headerAlign: 'right', renderCell: params => `₹${formatINR(Number(params.row.amount || params.row.TaxableAmt || 0))}` },
        {
            width: 48, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: params => (
                <IconButton size="small" onClick={() => deleteRow(params.row.index)} color="error">
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <Paper sx={{ p: isSmall ? 1 : 1, overflowX: 'hidden' }}>
            <Box display="flex" flexDirection={isSmall ? 'column' : 'row'} gap={2} sx={{ width: '100%', boxSizing: 'border-box', flexWrap: isSmall ? 'wrap' : 'nowrap', overflow: 'hidden' }}>
                <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%', maxWidth: isSmall ? '100%' : 420, boxSizing: 'border-box', p: 2, borderRight: isSmall ? 'none' : '1px solid #e0e0e0' }} >
                    <Box>
                        <DropdownProductCode id="productCode" options={allProducts} value={current.productCode} onSelect={handleSelect} onNewProductAdded={loadAllProducts}  connectionString={connectionString} />
                        {!!errors.productCode && (<Typography color="error" variant="caption"> {errors.productCode} </Typography>)}
                    </Box>

                    <TextField label="Description" fullWidth multiline minRows={1} size="small" placeholder="Enter product description" value={current.description} onChange={handleChange('description')} sx={{ '& .MuiInputBase-root:not(.MuiInputBase-multiline)': { height: 40 }, '& .MuiOutlinedInput-inputMultiline': { padding: '8px 12px', minHeight: '80px' } }} />

                    <Box display="flex" flexDirection="row" gap={1.5}>
                        <TextField label="Unit" select fullWidth size='small' value={String(current.UnitId)} onChange={handleChange('UnitId')} SelectProps={{ native: true }} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center' } }} >
                            <option value=""></option>
                            {unitOptions.map(u => (
                                <option key={u.UnitId} value={u.UnitId}>
                                    {u.unit}
                                </option>
                            ))}
                        </TextField>
                        <TextField label="Tax(%)" fullWidth type="number" disabled value={current.tax} onChange={handleChange('tax')} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center' } }} />
                    </Box>

                    <Box>
                        {/* Size preset select */}
                        <TextField label="Size Label" select fullWidth size="small" value={selectedSizePreset} onChange={(e) => setSelectedSizePreset(e.target.value)} SelectProps={{ native: true }} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center' } }}>
                            <option value="male">{SIZE_PRESETS.male.label}</option>
                            <option value="kids">{SIZE_PRESETS.kids.label}</option>
                            <option value="female">{SIZE_PRESETS.female.label}</option>
                        </TextField>
                    </Box>

                    <Box>
                        {/* Color selector (opens dialog) */}
                        <TextField label="Color(s)" fullWidth size="small" value={selectedColors.map(id => (availableColors.find(c => c.id === id)?.name || id)).join(', ')} onClick={openColorDialog} InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center', cursor: 'pointer' } }} />
                        {!!errors.colors && (<Typography color="error" variant="caption">{errors.colors}</Typography>)}
                    </Box>

                    <Box display="flex" flexDirection="row" gap={1.5}>
                        <TextField label="Qty (auto)" fullWidth type="number" inputProps={{ min: 0 }} value={current.qty} onChange={handleChange('qty')} error={!!errors.qty} helperText={errors.qty} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center' } }} disabled />
                        <TextField label="Rate" fullWidth type="number" inputProps={{ min: 0 }} value={current.rate} onChange={handleChange('rate')} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center' } }} />
                        <TextField label="Disc%" fullWidth type="number" inputProps={{ min: 0, max: 100 }} value={current.disc} onChange={handleChange('disc')} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center' } }} />
                    </Box>
                    <TextField label="Amount" fullWidth disabled type="number" size="small" value={current.amount} sx={{ '& .MuiOutlinedInput-root': { height: 40, alignItems: 'center' } }} />
                    <Box> <TextField label="Remark" fullWidth multiline minRows={1} maxRows={3} size="small" value={current.remark || ''} onChange={handleChange('remark')} sx={{ '& .MuiOutlinedInput-root': { height: 40, resize: 'vertical', overflow: 'hidden' } }} /> </Box>
                    <Box display="flex" justifyContent="center">
                        <Button variant="outlined" onClick={handleOpenMatrix} sx={{ minWidth: 180 }}>
                            {editIndex != null ? 'Update Product' : 'Add Product'}
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ width: isSmall ? '100%' : 670, maxWidth: '100%', p: isSmall ? 0 : 0, marginTop: isSmall ? 2 : -4, flexShrink: 0 }}>
                    <DataGrid rows={rows.map((r, i) => ({ ...r, id: i + 1, index: i }))} columns={columns} getRowHeight={() => 'auto'} autoHeight disableRowSelectionOnClick hideFooter
                        sx={{
                            mt: 5, border: '1px solid #cbd5e1', borderRadius: 1,
                            '& .MuiDataGrid-columnHeaders': { minHeight: 36, maxHeight: 36 },
                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#f0f0f0', paddingTop: '4px', paddingBottom: '4px', height: 42, lineHeight: '1.2rem', fontSize: '13px' },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#edf2feff' },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    />
                    <Box mt={3}>
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Basic Amount</Typography>
                            <Typography>₹{formatINR(totals.basic)}</Typography>
                        </Grid>
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Discount Amount</Typography>
                            <Typography>₹{formatINR(totals.discountAmount)}</Typography>
                        </Grid>
                        {state === compState ? (
                            <>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>CGST</Typography>
                                    <Typography>₹{formatINR(totals.cgst)}</Typography>
                                </Grid>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>SGST</Typography>
                                    <Typography>₹{formatINR(totals.sgst)}</Typography>
                                </Grid>
                            </>
                        ) : (
                            totals.igst > 0 && (
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>IGST</Typography>
                                    <Typography>₹{formatINR(totals.igst)}</Typography>
                                </Grid>
                            )
                        )}
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Round Off (+)</Typography>
                            <Typography>₹{formatINR(totals.roundOff)}</Typography>
                        </Grid>
                        <Divider sx={{ my: 1 }} />
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600}>Total Amount</Typography>
                            <Typography fontWeight={600}>₹{formatINR(totals.total)}</Typography>
                        </Grid>
                    </Box>
                </Box>
            </Box>

            {!!errors.global && (<Typography color="error" sx={{ mt: 2 }}> {Array.isArray(errors.global) ? errors.global.join(' | ') : errors.global} </Typography>)}

            <Box mt={3} display="flex" justifyContent="flex-end">
                <Button variant="contained" onClick={handleNext}> Next </Button>
            </Box>

            {/* Color Selection Dialog */}
            <Dialog open={colorDialogOpen} onClose={closeColorDialog} fullWidth maxWidth="sm">
                <DialogTitle>Select Colors</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {availableColors.map(c => {
                            const checked = selectedColors.includes(c.id);
                            return (
                                <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: checked ? '1px solid #1976d2' : '1px solid #eee', borderRadius: 1, padding: 1 }}>
                                    <FormControlLabel control={<Checkbox checked={checked} onChange={() => toggleColorSelection(c.id)} />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 20, height: 20, bgcolor: c.hex, border: c.hex === '#ffffff' ? '1px solid #cfcfcf' : undefined }} />
                                                <Typography variant="body2">{c.name}</Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                    <Box mt={2}>
                        <Typography variant="caption" color="text.secondary">Tip: You can add colors from your color master & adapt the list</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeColorDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Matrix Dialog */}
            <Dialog open={matrixDialogOpen} onClose={() => setMatrixDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Color-Size Quantity Matrix</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography>Selected Size Preset: {SIZE_PRESETS[selectedSizePreset].label}</Typography>
                        <Typography>Selected Colors: {selectedColors.length > 0 ? selectedColors.map(id => availableColors.find(c => c.id === id)?.name || id).join(', ') : (editIndex !== null && (rows[editIndex]?.sizeBreakdown?.default || rows[editIndex]?.sizeQtys) ? 'Default (legacy)' : 'None')}</Typography>
                        {Object.keys(tempMatrix).length === 0 ? (
                            <Typography color="error">Select colors first</Typography>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Grid container sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                                    <Grid item xs={2} sx={{ borderBottom: '1px solid #ddd', p: 1, fontWeight: 'bold' }}>Color</Grid>
                                    {SIZE_PRESETS[selectedSizePreset].sizes.map(s => (
                                        <Grid item xs={1} key={s} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>{s}</Grid>
                                    ))}
                                    <Grid item xs={1} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>Total</Grid>
                                    {Object.keys(tempMatrix).map(cId => {
                                        const colorObj = cId === 'default' ? { id: 'default', name: 'Default', hex: '#ccc' } : (availableColors.find(c => c.id === cId) || { id: cId, name: cId, hex: '#ccc' });
                                        const rowTotal = Object.values(tempMatrix[cId] || {}).reduce((s, v) => s + Number(v || 0), 0);
                                        return (
                                            <Grid container key={cId}>
                                                <Grid item xs={2} sx={{ borderBottom: '1px solid #ddd', p: 1, display: 'flex', alignItems: 'center' }}>
                                                    <Avatar sx={{ mr: 1, width: 24, height: 24, bgcolor: colorObj.hex, border: colorObj.hex === '#ffffff' ? '1px solid #cfcfcf' : undefined }} />
                                                    {colorObj.name}
                                                </Grid>
                                                {SIZE_PRESETS[selectedSizePreset].sizes.map(s => (
                                                    <Grid item xs={1} key={s} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 0.5, textAlign: 'center' }}>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            inputProps={{ min: 0 }}
                                                            value={tempMatrix[cId]?.[s] || 0}
                                                            onChange={e => setTempMatrix(prev => ({ ...prev, [cId]: { ...prev[cId], [s]: Number(e.target.value) || 0 } }))}
                                                            sx={{ width: '100%', '& .MuiInputBase-input': { textAlign: 'center', p: 0.5 } }}
                                                        />
                                                    </Grid>
                                                ))}
                                                <Grid item xs={1} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center' }}>{rowTotal}</Grid>
                                            </Grid>
                                        );
                                    })}
                                    <Grid item xs={2} sx={{ p: 1, fontWeight: 'bold' }}>Total</Grid>
                                    {SIZE_PRESETS[selectedSizePreset].sizes.map(s => {
                                        const colTotal = Object.keys(tempMatrix).reduce((sum, cId) => sum + Number(tempMatrix[cId]?.[s] || 0), 0);
                                        return <Grid item xs={1} key={s} sx={{ borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>{colTotal}</Grid>;
                                    })}
                                    <Grid item xs={1} sx={{ borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                        {Object.keys(tempMatrix).reduce((sum, cId) => sum + Object.values(tempMatrix[cId] || {}).reduce((s, v) => s + Number(v || 0), 0), 0)}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        {!!matrixError && <Typography color="error">{matrixError}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMatrixDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmMatrix} variant="contained">Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Size Breakdown Dialog */}
            <Dialog open={sizeDialogOpen} onClose={closeSizeDialog} fullWidth maxWidth="md">
                <DialogTitle>Size Breakdown - {selectedRow?.productCode}</DialogTitle>
                <DialogContent>
                    {selectedRow ? (
                        <Box sx={{ overflowX: 'auto' }}>
                            {(() => {
                                const rowBreakdown = selectedRow.sizeBreakdown || (selectedRow.sizeQtys ? { default: selectedRow.sizeQtys } : {});
                                const preset = SIZE_PRESETS[selectedRow.sizePresetKey || 'male'];
                                const breakdownKeys = Object.keys(rowBreakdown);
                                if (breakdownKeys.length === 0) {
                                    return <Typography>No size breakdown available.</Typography>;
                                }
                                return (
                                    <Grid container sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                                        <Grid item xs={2} sx={{ borderBottom: '1px solid #ddd', p: 1, fontWeight: 'bold' }}>Color</Grid>
                                        {preset.sizes.map(s => (
                                            <Grid item xs={1} key={s} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>{s}</Grid>
                                        ))}
                                        <Grid item xs={1} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>Total</Grid>
                                        {breakdownKeys.map(cId => {
                                            const colorObj = cId === 'default' ? { id: 'default', name: 'Default', hex: '#ccc' } : (availableColors.find(c => c.id === cId) || { id: cId, name: cId, hex: '#ccc' });
                                            const rowTotal = preset.sizes.reduce((sum, s) => sum + Number(rowBreakdown[cId][s] || 0), 0);
                                            return (
                                                <Grid container key={cId}>
                                                    <Grid item xs={2} sx={{ borderBottom: '1px solid #ddd', p: 1, display: 'flex', alignItems: 'center' }}>
                                                        <Avatar sx={{ mr: 1, width: 24, height: 24, bgcolor: colorObj.hex, border: colorObj.hex === '#ffffff' ? '1px solid #cfcfcf' : undefined }} />
                                                        {colorObj.name}
                                                    </Grid>
                                                    {preset.sizes.map(s => (
                                                        <Grid item xs={1} key={s} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center' }}>
                                                            <Typography variant="body2">{rowBreakdown[cId][s] || 0}</Typography>
                                                        </Grid>
                                                    ))}
                                                    <Grid item xs={1} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center' }}>
                                                        <Typography variant="body2" fontWeight="bold">{rowTotal}</Typography>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                        <Grid item xs={2} sx={{ p: 1, fontWeight: 'bold' }}>Total</Grid>
                                        {preset.sizes.map(s => {
                                            const colTotal = breakdownKeys.reduce((sum, cId) => sum + Number(rowBreakdown[cId]?.[s] || 0), 0);
                                            return <Grid item xs={1} key={s} sx={{ borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>{colTotal}</Grid>;
                                        })}
                                        <Grid item xs={1} sx={{ borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                            {breakdownKeys.reduce((sum, cId) => sum + preset.sizes.reduce((s, sz) => s + Number(rowBreakdown[cId][sz] || 0), 0), 0)}
                                        </Grid>
                                    </Grid>
                                );
                            })()}
                        </Box>
                    ) : (
                        <Typography>Loading...</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSizeDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ProductDetails;